import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Drawer, useMediaQuery } from "@mui/material";
import { RiHomeLine, RiUserSmileLine } from "@remixicon/react";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions } from "../../config/permissions";
import { getAttentionCounts } from "../../services/dataService";
import { inboxBadgeCount } from "../../utils/inboxPresentation";
import { activeMenuPath, breadcrumbTrail } from "../../utils/shellPresentation";
import { tokens } from "../../utils/figmaTokens";
import SearchOverlay from "../SearchOverlay/SearchOverlay";
import { NAV_SECTIONS } from "./navSections";
import { getMenuItems, SECTION_ICONS } from "./menuItems";
import { BreadcrumbContext } from "./BreadcrumbContext";
import Sidebar, { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from "./Sidebar";
import TopBar from "./TopBar";

const CRUMB_ICONS = { home: RiHomeLine, person: RiUserSmileLine, sections: SECTION_ICONS };

// The shell (047 D10–D13): Figma's sidebar and top bar around every page, and the grey
// rounded page area. The menu, who sees what, the Inbox count, Ctrl/Cmd+K and sign-out
// work as before (044 D1, 024 D4). On a phone the sidebar is a drawer the top bar opens.
function Layout({ children }) {
  const phone = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [crumbNames, setCrumbNames] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const userRole = user?.role || "";

  // Inbox nav badge — open-ticket count, fetched once per session and only
  // for roles that can actually see the Inbox link. Reuses the same
  // attention-queue aggregation the Dashboard tile does (dataService's own
  // count() query); no second aggregation is written for this.
  const [inboxOpenCount, setInboxOpenCount] = useState(0);
  useEffect(() => {
    if (!hasPermission(userRole, permissions.VIEW_INBOX)) return;
    let cancelled = false;
    getAttentionCounts()
      .then((counts) => {
        if (!cancelled) setInboxOpenCount(inboxBadgeCount(counts));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [userRole]);

  // Ctrl+K / Cmd+K people search (spec 024 D4/T9) — works from anywhere in
  // the dashboard, not just the pages that render a search box.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== "k") return;
      // Don't steal the shortcut while the admin is typing — every edit
      // dialog and every page's own search box is a text input the global
      // listener would otherwise yank focus away from (review round 1).
      const target = e.target;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) {
        return;
      }
      e.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // A page hands up the names its breadcrumb needs (BreadcrumbContext).
  const setCrumbName = useCallback(
    (key, name) => setCrumbNames((names) => (names[key] === name ? names : { ...names, [key]: name })),
    []
  );

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch (e) {}
  };

  const menuItems = getMenuItems(user, inboxOpenCount);
  const activePath = activeMenuPath(location.pathname, menuItems.map((item) => item.path));
  const crumbs = breadcrumbTrail(location.pathname, {
    items: menuItems,
    sections: NAV_SECTIONS,
    names: crumbNames,
    icons: CRUMB_ICONS,
  });
  const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
  const openSearch = () => {
    setMobileOpen(false);
    setSearchOpen(true);
  };
  const sidebar = (rail) => (
    <Sidebar
      items={menuItems}
      activePath={activePath}
      collapsed={rail}
      user={user}
      onNavigate={() => setMobileOpen(false)}
      onSearch={openSearch}
      onLogout={handleLogout}
    />
  );

  return (
    <Box sx={{ display: "flex", width: "100%", minHeight: "100vh", bgcolor: tokens.surface.default }}>
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

      {phone ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ "& .MuiDrawer-paper": { width: SIDEBAR_WIDTH, border: "none" } }}
        >
          {sidebar(false)}
        </Drawer>
      ) : (
        <Box component="aside" sx={{ width, flexShrink: 0, transition: "width 0.2s ease" }}>
          <Box sx={{ position: "fixed", top: 0, bottom: 0, left: 0, width, overflow: "hidden", transition: "width 0.2s ease" }}>
            {sidebar(collapsed)}
          </Box>
        </Box>
      )}

      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <Box sx={{ position: "sticky", top: 0, zIndex: (theme) => theme.zIndex.appBar }}>
          <TopBar
            crumbs={crumbs}
            collapsed={phone ? !mobileOpen : collapsed}
            onToggle={() => (phone ? setMobileOpen((open) => !open) : setCollapsed((c) => !c))}
            onSearch={openSearch}
          />
        </Box>
        {/* Figma's Main (10724:14550): grey, rounded, flush against the sidebar and the top bar. */}
        <Box component="main" sx={{ flex: 1, minWidth: 0, bgcolor: tokens.surface.page, borderRadius: "16px", p: { xs: 2, md: 4 } }}>
          <BreadcrumbContext.Provider value={setCrumbName}>{children}</BreadcrumbContext.Provider>
        </Box>
      </Box>
    </Box>
  );
}

export default Layout;
